import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { ExerciseLockedRequiredPlan, LockedContentKind } from './ExerciseLockedModal';

export interface ExerciseLockedInlineProps {
  exerciseTitle?: string;
  requiredPlans: ExerciseLockedRequiredPlan[];
  className?: string;
  /** Drives the heading + body wording. Defaults to "Exercise" for backward compatibility. */
  contentKind?: LockedContentKind;
}

const HEADING_KEY: Record<LockedContentKind, string> = {
  Course: 'headingCourse',
  Level: 'headingLevel',
  Lesson: 'headingLesson',
  Exercise: 'headingExercise',
  Book: 'headingBook',
};

const KIND_KEY: Record<LockedContentKind, string> = {
  Course: 'kindCourse',
  Level: 'kindLevel',
  Lesson: 'kindLesson',
  Exercise: 'kindExercise',
  Book: 'kindBook',
};

/**
 * Inline variant of the exercise subscription lock (same Lottie + plan hints as ExerciseLockedModal).
 */
export const ExerciseLockedInline: React.FC<ExerciseLockedInlineProps> = ({
  exerciseTitle,
  requiredPlans,
  className = '',
  contentKind = 'Exercise',
}) => {
  const { t } = useTranslation('learning');
  const [animationError, setAnimationError] = useState(false);
  const kindLabel = t(`contentLocked.${KIND_KEY[contentKind]}`);
  const heading = t(`contentLocked.${HEADING_KEY[contentKind]}`);

  return (
    <div
      className={`rounded-2xl border border-amber-200/90 bg-gradient-to-br from-amber-50/95 via-white to-orange-50/60 p-6 text-center shadow-sm dark:border-amber-900/40 dark:from-amber-950/30 dark:via-slate-900/80 dark:to-slate-950 sm:p-8 ${className}`}
      role="region"
      aria-labelledby="inline-exercise-locked-heading"
    >
      <div className="mx-auto mb-4 flex h-28 w-28 items-center justify-center sm:h-36 sm:w-36">
        {animationError ? (
          <Lock className="h-20 w-20 text-amber-600/70 dark:text-amber-400/70 sm:h-28 sm:w-28" aria-hidden />
        ) : (
          <DotLottieReact
            src="/animations/Locked_Icon.lottie"
            loop
            autoplay
            className="h-full w-full"
            onError={() => setAnimationError(true)}
          />
        )}
      </div>
      <h3
        id="inline-exercise-locked-heading"
        className="mb-2 text-lg font-bold text-gray-900 dark:text-gray-100 sm:text-xl"
      >
        {heading}
      </h3>
      {exerciseTitle ? (
        <p className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">{exerciseTitle}</p>
      ) : null}
      <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">
        {t('contentLocked.requiresPlan', { kind: kindLabel })}
      </p>
      {requiredPlans.length > 0 ? (
        <div className="mb-4 text-start">
          <p className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            {t('contentLocked.requiredPlans', { count: requiredPlans.length })}
          </p>
          <div className="space-y-2">
            {requiredPlans.map((plan) => (
              <div
                key={plan.id}
                className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20"
              >
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">{plan.name}</p>
                {plan.description ? (
                  <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">{plan.description}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
        {requiredPlans.length > 0
          ? t('contentLocked.noPlanAccess', { count: requiredPlans.length, kind: kindLabel })
          : t('contentLocked.cannotAccess', { kind: kindLabel })}
      </p>
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(
            new CustomEvent('app:navigate', { detail: { view: 'profile', hash: 'subscription' } })
          );
        }}
        className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
      >
        {t('contentLocked.goToPlans')}
      </button>
    </div>
  );
};
